import type { ForwardedRef, KeyboardEvent } from 'react';
import { forwardRef } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { breakpoints, vars } from '@etg/wings';
import { BrandLogo } from '@eti/components';
import { useProperty } from '@eti/providers';
import { css } from '@eti/styles';
import { constants } from '@eti/utils';
import { useLocation } from 'react-router-dom';
import { routes } from '../../constants/routesAndApisConstants';
import NarrowSiteLogo from './NarrowSiteLogo';

const { brands } = constants;

export const getLogoHeightDesktop = (brand: string, siteName: string) => {
  if (siteName === 'flybillet_dk') {
    return '32px';
  }
  switch (brand) {
    case brands.GOTOGATE:
      return '20px';
    case brands.AZULMUNDO:
    case brands.FLIGHTNETWORK:
      return '28px';
    case brands.JOURNEYDETAILS:
    case brands.RADISSONHOTELS:
    case brands.MYTRIP:
    case brands.RCI:
    case brands.WYNDHAM:
      return '32px';
    case brands.BOOKING:
    case brands.SERKO:
    case brands.TRAVELFINDER:
      return '35px';
    case brands.HOTELIERMART:
    case brands.SEAT24:
    case brands.TRAVELSTART:
    case brands.TRIPBEAT:
      return '40px';
    case brands.FLYFAR:
      return '45px';
    case brands.ROME2RIO:
    case brands.TUI:
      return '48px';
    case brands.SUBIRTE:
    case brands.TRIP:
      return '50px';
    case brands.SUPERSAVER:
      return '65px';
    default:
      return '20px';
  }
};

const getLogoHeightMobile = (brand: string, siteName: string) => {
  if (siteName === 'flybillet_dk') {
    return '32px';
  }
  switch (brand) {
    case brands.AZULMUNDO:
    case brands.MYTRIP:
      return '24px';
    case brands.TRAVELSTART:
      return '25px';
    case brands.JOURNEYDETAILS:
    case brands.GOTOGATE:
    case brands.FLIGHTNETWORK:
    case brands.TRAVELFINDER:
      return '28px';
    case brands.FLYFAR:
    case brands.SEAT24:
      return '30px';
    case brands.RADISSONHOTELS:
      return '32px';
    case brands.ROME2RIO:
      return '38px';
    default:
      return '35px';
  }
};

const brandLogoStyles = (brand: string, siteName: string) => css`
  height: ${getLogoHeightMobile(brand, siteName)};
  max-width: 156px;
  pointer-events: none;

  @media (min-width: 60.625em) {
    height: ${getLogoHeightDesktop(brand, siteName)};
    max-width: 200px;
  }
`;

const getWrapperClass = (brand: string) => css`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  justify-content: space-between;
  margin-top: ${brand === brands.SUPERSAVER ? '5px;' : '0'};

  &:focus-visible {
    outline: 2px solid ${vars.colors.header.content};
    outline-offset: 2px;
  }

  @media (min-width: ${breakpoints._768}) {
    margin: ${brand === brands.SUPERSAVER ? '10px 0 0' : '10px 0'};
  }
`;

// TODO Remove/Move when the Skip link is available in production.
const focusBackToCookieBanner = (event: KeyboardEvent) => {
  const internalCookieBannerExists = document.getElementById('etgCookieBanner') != null;
  const isShiftTab = event.shiftKey && event.key === 'Tab';

  if (internalCookieBannerExists && isShiftTab) {
    event.preventDefault();
    document.getElementById('cookieBanner-closeButton')?.focus();
  }
};

interface SiteLogoInterface {
  baseUrl: string;
  brand: string;
  siteName: string;
}

const SiteLogo = forwardRef<HTMLElement, SiteLogoInterface>(
  ({ baseUrl, brand, siteName }: SiteLogoInterface, ref) => {
    const { p } = useProperty();
    const { pathname } = useLocation();
    const isNarrowScreen = useMediaQuery(`(max-width: ${breakpoints._768})`);

    const shouldRenderAlternateAzulLogo =
      isNarrowScreen &&
      pathname.includes(routes.RESULT) &&
      p('Result.AlternateSearchBar.Enabled') &&
      brand === brands.AZULMUNDO;

    const brandLogo = shouldRenderAlternateAzulLogo ? (
      <NarrowSiteLogo brand={brand} />
    ) : (
      <BrandLogo brand={brand} className={brandLogoStyles(brand, siteName)} siteName={siteName} />
    );

    return baseUrl ? (
      <a
        className={getWrapperClass(brand)}
        data-testid="site-logo"
        href={baseUrl || undefined}
        id="site-logo"
        onKeyDown={focusBackToCookieBanner}
        ref={ref as ForwardedRef<HTMLAnchorElement>}
      >
        {brandLogo}
      </a>
    ) : (
      // oxlint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        className={getWrapperClass(brand)}
        data-testid="site-logo"
        id="site-logo"
        onKeyDown={focusBackToCookieBanner}
        ref={ref as ForwardedRef<HTMLDivElement>}
      >
        {brandLogo}
      </div>
    );
  },
);

export default SiteLogo;
